import { apiClient, handleApiError } from './api.js';

class InventoryService {
  // ==================== Inventory Categories ====================
  
  // Get all inventory categories
  async getAllCategories() {
    try {
      return await apiClient.get('/inventory/categories');
    } catch (error) {
      console.error('Error getting all categories:', error);
      throw error;
    }
  }

  // Get inventory category by ID
  async getCategoryById(categoryId) {
    try {
      return await apiClient.get(`/inventory/categories/${categoryId}`);
    } catch (error) {
      console.error(`Error getting category ${categoryId}:`, error);
      throw error;
    }
  }

  // Create a new inventory category
  async createCategory(category) {
    try {
      // Prepare category data with user tracking fields
      const categoryData = {
        ...category,
        // Initialize navigation properties for user tracking
        // These will be populated by the backend based on the JWT token
        // Providing empty User objects with UserId property to satisfy model validation
        createdByUser: { userId: 0 },
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.post('/inventory/categories', categoryData);
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  // Update an existing inventory category
  async updateCategory(categoryId, category) {
    try {
      // The backend will handle timestamp tracking and user tracking via the JWT token
      // UpdatedBy and UpdatedAt are extracted from the JWT token in the backend
      const categoryData = {
        ...category,
        // Initialize navigation property for user tracking
        // This will be populated by the backend based on the JWT token
        // Providing empty User object with UserId property to satisfy model validation
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.put(`/inventory/categories/${categoryId}`, categoryData);
    } catch (error) {
      console.error(`Error updating category ${categoryId}:`, error);
      throw error;
    }
  }

  // Delete an inventory category
  async deleteCategory(categoryId) {
    try {
      return await apiClient.delete(`/inventory/categories/${categoryId}`);
    } catch (error) {
      console.error(`Error deleting category ${categoryId}:`, error);
      throw error;
    }
  }

  // ==================== Inventory Items ====================
  
  // Get all inventory items
  async getAllItems() {
    try {
      return await apiClient.get('/inventory/items');
    } catch (error) {
      console.error('Error getting all items:', error);
      throw error;
    }
  }

  // Get inventory item by ID
  async getItemById(itemId) {
    try {
      return await apiClient.get(`/inventory/items/${itemId}`);
    } catch (error) {
      console.error(`Error getting item ${itemId}:`, error);
      throw error;
    }
  }

  // Get inventory item by SKU
  async getItemBySku(sku) {
    try {
      return await apiClient.get(`/inventory/items/sku/${sku}`);
    } catch (error) {
      console.error(`Error getting item by SKU ${sku}:`, error);
      throw error;
    }
  }

  // Get inventory items by category
  async getItemsByCategory(categoryId) {
    try {
      return await apiClient.get(`/inventory/items/category/${categoryId}`);
    } catch (error) {
      console.error(`Error getting items for category ${categoryId}:`, error);
      throw error;
    }
  }

  // Get inventory items with low stock (below reorder level)
  async getLowStockItems() {
    try {
      return await apiClient.get('/inventory/items/low-stock');
    } catch (error) {
      console.error('Error getting low stock items:', error);
      throw error;
    }
  }

  // Create a new inventory item
  async createItem(item) {
    try {
      // Prepare item data with user tracking fields
      const itemData = {
        ...item,
        // Initialize navigation properties for user tracking
        // These will be populated by the backend based on the JWT token
        // Providing empty User objects with UserId property to satisfy model validation
        createdByUser: { userId: 0 },
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.post('/inventory/items', itemData);
    } catch (error) {
      console.error('Error creating item:', error);
      throw error;
    }
  }

  // Update an existing inventory item
  async updateItem(itemId, item) {
    try {
      // The backend will handle timestamp tracking and user tracking via the JWT token
      // UpdatedBy and UpdatedAt are extracted from the JWT token in the backend
      const itemData = {
        ...item,
        // Initialize navigation property for user tracking
        // This will be populated by the backend based on the JWT token
        // Providing empty User object with UserId property to satisfy model validation
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.put(`/inventory/items/${itemId}`, itemData);
    } catch (error) {
      console.error(`Error updating item ${itemId}:`, error);
      throw error;
    }
  }

  // Delete an inventory item
  async deleteItem(itemId) {
    try {
      return await apiClient.delete(`/inventory/items/${itemId}`);
    } catch (error) {
      console.error(`Error deleting item ${itemId}:`, error);
      throw error;
    }
  }

  // Update inventory item stock
  async updateStock(itemId, quantity) {
    try {
      return await apiClient.put(`/inventory/items/${itemId}/stock`, { quantity });
    } catch (error) {
      console.error(`Error updating stock for item ${itemId}:`, error);
      throw error;
    }
  }

  // ==================== Inventory Transactions ====================
  
  // Get all inventory transactions
  async getAllTransactions() {
    try {
      return await apiClient.get('/inventory/transactions');
    } catch (error) {
      console.error('Error getting all transactions:', error);
      throw error;
    }
  }

  // Get inventory transactions by item ID
  async getTransactionsByItemId(itemId) {
    try {
      return await apiClient.get(`/inventory/transactions/item/${itemId}`);
    } catch (error) {
      console.error(`Error getting transactions for item ${itemId}:`, error);
      throw error;
    }
  }

  // Get inventory transaction by ID
  async getTransactionById(transactionId) {
    try {
      return await apiClient.get(`/inventory/transactions/${transactionId}`);
    } catch (error) {
      console.error(`Error getting transaction ${transactionId}:`, error);
      throw error;
    }
  }

  // Create a new inventory transaction
  async createTransaction(transaction) {
    try {
      return await apiClient.post('/inventory/transactions', transaction);
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  }
}

export default new InventoryService();